import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import LaunchIcon from '@mui/icons-material/Launch';
import { Button } from '@mui/material';
import { FormikProps } from 'formik';

// components
import BasicChip from '../../components/display/chips/basicChip';
import BasicModal from '../../components/modals/basicModal';
import AampeSavingInfoIndicator from '../../components/shared/AampeSavingInfoIndicator/AampeSavingInfoIndicator';
import FormulaTagsView from '../../components/shared/FormulaTagsView';
import styled from 'styled-components';

// constants
import aampeTheme from '../../constants/aampeTheme';
import { FormulaType } from '../../constants/formulaType';
import routes from '../../constants/routes';

// contexts
import { EditorContext } from '../../context/EditorContext';

// helpers
import { formatUTCDate } from '../../helpers/dateTimeHelpers';
import {
    getFormulaScheduleTimeText,
    getFormulaSchedulingType,
    getFormulaStartEndDate,
} from '../../helpers/formulaHelpers';
import { titlecase } from '../../helpers/sanitize.helper';
import { getTimeZoneOptions } from '../../helpers/timezoneHelper';

import useQueryEmailTemplates from '../../hooks/EmailTemplate/useQueryFetchTemplates';
// hooks
import useAudiencesDetailQuery from '../../hooks/audiences/useAudiencesDetailQuery';
import useReachableUsers from '../../hooks/audiences/useReachableUsers';
import useComponentTypesQuery from '../../hooks/component-types/useComponentTypesQuery';
import useCustomEmailTemplateDetail from '../../hooks/custom-email-templates/useCustomEmailTemplateDetail';
import useIsCustomerIOPushProvider from '../../hooks/formulas/useIsCustomerIOPushProvider';
import useIsProviderFrequencyLimitOverrideAllowed from '../../hooks/formulas/useIsProviderFrequencyLimitOverrideAllowed';
import useFormulaTimingSlots from '../../hooks/timing-slots/useTimingSlots';

// models
import {
    ContentCardType,
    FormulaDetailModel,
    FormulaSchedulingOptions,
    FormulaSchedulingOptionsLabels,
    FormulaStartStopType,
    FormulaTypesData,
    FormulaViewSetupFormValue,
    LabelVariantTypeValue,
} from '../../models/formula.model';
import { TimingSlotsModel } from '../../models/timing-slots.model';

import TestFormulaModal from './formulaEditor/assistant/testFormula/TestFormulaModal';
import MessageTimingConstraints from './formulaSettings/MessageTimingConstraints';
import MessagePreviewMain from './messagePreview/MessagePreviewMain';

interface StyledProps {
    color?: string;
}
export interface FormulaFinalCheckProps {
    isTestMessageClick?: boolean;
    isFormulaView?: boolean;
    setIsTestMessageClick?: (isTestMessageClick: boolean) => void;
    formikProps?: FormikProps<FormulaViewSetupFormValue>;
}

const FormulaFinalCheck: React.FC<
    React.PropsWithChildren<FormulaFinalCheckProps>
> = ({
    isFormulaView,
    isTestMessageClick,
    setIsTestMessageClick,
    formikProps,
}) => {
    const { data: timingSlots } = useFormulaTimingSlots();
    const { data: componentTypes } = useComponentTypesQuery();
    const {
        editorDetails: { formula, isControlledFormula, formulaType },
    } = useContext(EditorContext);

    const { isFrequencyLimitAllowed } =
        useIsProviderFrequencyLimitOverrideAllowed();

    const { isCustomerIOPushProvider } = useIsCustomerIOPushProvider();

    const { data: audiences } = useAudiencesDetailQuery();
    const { data } = useReachableUsers(formula as FormulaDetailModel);

    const emailTemplateId = useMemo<string | undefined>(
        () => formula?.additional_metadata?.email_template_id,
        [formula],
    );
    const isAampeCustomTemplate = useMemo<boolean>(
        () => !!formula?.additional_metadata?.is_aampe_custom_template,
        [formula],
    );
    const isEmailFormula = formulaType === FormulaType.email;

    const { data: emailTemplates } = useQueryEmailTemplates(
        isEmailFormula && !isAampeCustomTemplate,
    );
    const { data: customTemplateData } = useCustomEmailTemplateDetail(
        emailTemplateId as string,
        isEmailFormula && isAampeCustomTemplate,
    );

    const emailTemplateName = useMemo<string | undefined>(() => {
        if (!isEmailFormula || !emailTemplateId) return undefined;
        if (isAampeCustomTemplate) return customTemplateData?.name;
        return emailTemplates?.find(
            (t) => t.email_template_id === emailTemplateId,
        )?.template_name;
    }, [
        isEmailFormula,
        emailTemplateId,
        isAampeCustomTemplate,
        customTemplateData,
        emailTemplates,
    ]);

    const [showTimeSlotsModal, setShowTimeSlotsModal] =
        useState<boolean>(false);

    const filteredAudienceCount = useMemo<number>(
        () => data?.total_filtered_audience || 0,
        [data],
    );
    const isSupportTrigger = useMemo<boolean | undefined>(() => {
        return FormulaTypesData[formulaType as FormulaType]?.supportTrigger;
    }, [formulaType]);

    const schedulingType = useMemo<FormulaSchedulingOptions>(
        () =>
            getFormulaSchedulingType(
                timingSlots as TimingSlotsModel[],
                {
                    ...formula,
                    is_controlled_message: isControlledFormula,
                } as FormulaDetailModel,
            ),
        [formula, timingSlots, isControlledFormula],
    );

    const renderLabels = useMemo<LabelVariantTypeValue>(() => {
        const variantTypes = formula?.labels?.map(
            (label) => label.variant_type,
        );
        return (
            variantTypes?.reduce((acc, variant) => {
                const labelNames = formula?.labels
                    ?.filter((label) => label.variant_type === variant)
                    .map((label) => label.label_name);
                return { ...acc, [variant]: labelNames };
            }, {}) || {}
        );
    }, [formula]);

    const formulaScheduleData = useMemo<FormulaStartStopType>(
        () => getFormulaStartEndDate(formula),
        [formula],
    );

    const dateText = useMemo<string>(
        () =>
            getFormulaScheduleTimeText(
                isControlledFormula,
                formulaScheduleData.send_start,
                formulaScheduleData.send_stop,
                formula?.timezone,
            ),
        [formula, isControlledFormula, formulaScheduleData],
    );

    const timezoneText = useMemo<string>(() => {
        const timezone = getTimeZoneOptions().find(
            (item) => item.value === formula?.timezone,
        );
        return timezone?.label ?? '';
    }, [formula]);

    const handleTestMessageClose = () => {
        setIsTestMessageClick?.(false);
    };

    const contentCardType = useMemo<string | undefined>(() => {
        if (formulaType === FormulaType.contentCards) {
            return titlecase(
                formula?.content_card_type || ContentCardType.TextOnly,
            )?.replace(/_/g, ' ');
        }
        return undefined;
    }, [formulaType, formula]);

    const timeslotsLength = useMemo<number>(() => {
        if (isFormulaView && formikProps) {
            return formikProps?.values.time_slot_ids?.length || 0;
        }
        if (formula && formula?.time_slot_ids?.length) {
            return formula.time_slot_ids.length;
        }
        return timingSlots?.length || 0;
    }, [timingSlots, formula, formikProps, isFormulaView]);

    const getComponentType = useCallback(
        (labelType: string) => {
            return componentTypes?.map[labelType];
        },
        [componentTypes],
    );

    return (
        <MainContainer>
            <Container>
                <RowContainer>
                    <Property>Tags</Property>
                    <FormulaTagsView tags={formula?.tags} />
                </RowContainer>
                <RowContainer>
                    <Property>Your audience</Property>
                    {formula?.audiences?.length ? (
                        formula?.audiences?.map((audience) => {
                            return (
                                <PropertyValues key={audience?.audience_id}>
                                    {
                                        audiences?.find(
                                            (value) =>
                                                value.id ===
                                                audience.audience_id,
                                        )?.name
                                    }
                                </PropertyValues>
                            );
                        })
                    ) : (
                        <PropertyValues>All users</PropertyValues>
                    )}
                </RowContainer>
                {isSupportTrigger && (
                    <RowContainer>
                        <Property>Your trigger</Property>
                        <PropertyValues>
                            {formula?.compound_trigger?.name ?? '-'}
                        </PropertyValues>
                    </RowContainer>
                )}
                {contentCardType && (
                    <RowContainer>
                        <Property>Content card type</Property>
                        <PropertyValues>{contentCardType}</PropertyValues>
                    </RowContainer>
                )}
                {isEmailFormula && emailTemplateId && (
                    <RowContainer>
                        <Property>Email template used</Property>
                        <PropertyValues>
                            {emailTemplateName ? (
                                isAampeCustomTemplate ? (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        endIcon={
                                            <LaunchIcon fontSize="small" />
                                        }
                                        component={Link}
                                        to={routes.emailTemplateDetail(
                                            emailTemplateId,
                                        )}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                            textTransform: 'none',
                                            color: aampeTheme.colors
                                                .composerNXPrimary,
                                            borderColor:
                                                aampeTheme.colors
                                                    .composerNXPrimary,
                                            '&:hover': {
                                                borderColor:
                                                    aampeTheme.colors
                                                        .composerNXPrimary,
                                                backgroundColor:
                                                    'rgba(225, 94, 18, 0.04)',
                                            },
                                        }}
                                    >
                                        {emailTemplateName}
                                    </Button>
                                ) : (
                                    emailTemplateName
                                )
                            ) : (
                                '-'
                            )}
                        </PropertyValues>
                    </RowContainer>
                )}

                <RowContainer>
                    <Property>Your message labels</Property>
                    <PropertyDiv>
                        {(Object.keys(renderLabels) as string[])?.map(
                            (labelType) => {
                                const labels = renderLabels?.[labelType];
                                return (
                                    <PropertyValues key={labelType}>
                                        <LabelName
                                            color={
                                                getComponentType(labelType)
                                                    ?.base_color
                                            }
                                        >{`${
                                            getComponentType(labelType)?.name
                                        }: `}</LabelName>
                                        <LabelList>
                                            {labels?.map((label) => (
                                                <BasicChip
                                                    key={label}
                                                    title={label}
                                                    style={{ fontSize: '12px' }}
                                                />
                                            ))}
                                        </LabelList>
                                    </PropertyValues>
                                );
                            },
                        )}
                    </PropertyDiv>
                </RowContainer>

                <RowContainer>
                    <Property>Scheduling</Property>
                    <PropertyDiv>
                        <PropertyValues>
                            {FormulaSchedulingOptionsLabels[schedulingType]}
                        </PropertyValues>
                        {isFrequencyLimitAllowed && (
                            <PropertyValues>
                                {formula?.override_provider_frequency_limits
                                    ? 'Override delivery frequency limits'
                                    : 'Respect delivery frequency limits'}
                            </PropertyValues>
                        )}
                        {isCustomerIOPushProvider && (
                            <PropertyValues>
                                {formula?.send_to_unsubscribed
                                    ? 'Send to unsubscribed users'
                                    : 'Do not send to unsubscribed users'}
                            </PropertyValues>
                        )}
                    </PropertyDiv>
                </RowContainer>

                <RowContainer>
                    <Property>Time range</Property>
                    <PropertyValues>
                        {dateText}{' '}
                        {schedulingType ===
                            FormulaSchedulingOptions.AUTOMATED_WITH_GUARDRAILS && (
                            <ValueLink
                                onClick={() => setShowTimeSlotsModal(true)}
                            >
                                View {timeslotsLength} timeslots
                            </ValueLink>
                        )}
                    </PropertyValues>
                </RowContainer>

                {isControlledFormula && (
                    <RowContainer>
                        <Property>Timezone</Property>
                        <PropertyValues>{timezoneText}</PropertyValues>
                    </RowContainer>
                )}

                <RowContainer>
                    <Property>Reachable users</Property>
                    <PropertyValues>
                        {formula?.id
                            ? filteredAudienceCount?.toLocaleString()
                            : '-'}
                    </PropertyValues>
                </RowContainer>

                <RowContainer>
                    <Property>Alternate messages</Property>
                    <PropertyValues>
                        {formula?.message_count?.toLocaleString() ?? 0}
                    </PropertyValues>
                </RowContainer>
                {isFormulaView && (
                    <>
                        <RowContainer>
                            <Property>Creation date</Property>
                            <PropertyValues>
                                {formatUTCDate(
                                    formula?.created_at as string,
                                    'MMMM d, yyyy',
                                )}
                            </PropertyValues>
                        </RowContainer>
                        <RowContainer>
                            <Property>Last editing date</Property>
                            <PropertyValues>
                                {`${formatUTCDate(
                                    formula?.updated_at as string,
                                    'MMMM d, yyyy',
                                )} `}{' '}
                                <AampeSavingInfoIndicator
                                    updatedAt={formula?.updated_at}
                                    onlyValue
                                />
                            </PropertyValues>
                        </RowContainer>
                    </>
                )}
            </Container>
            <PreviewContainer>
                <MessagePreviewMain />
            </PreviewContainer>
            {isTestMessageClick && (
                <TestFormulaModal
                    handleClose={() => handleTestMessageClose()}
                />
            )}
            {showTimeSlotsModal && (
                <BasicModal
                    header="Time range"
                    width="500px"
                    handleClose={() => setShowTimeSlotsModal(false)}
                    headerStyle={{
                        justifyContent: 'space-between',
                        display: 'flex',
                        padding: aampeTheme.spacing4(4),
                    }}
                >
                    <CalenderContainer>
                        {timingSlots?.length && (
                            <MessageTimingConstraints
                                timingSlots={timingSlots}
                                formula={formula!}
                                isDisabled={true}
                                schedulingType={schedulingType}
                                formik={formikProps}
                            />
                        )}
                    </CalenderContainer>
                </BasicModal>
            )}
        </MainContainer>
    );
};

export default FormulaFinalCheck;

const MainContainer = styled.div({
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
});
const Container = styled.div({
    display: 'flex',
    width: '60%',
    alignItems: 'flex-start',
    gap: aampeTheme.spacing(3),
    flexDirection: 'column',
});

const RowContainer = styled.div({
    display: 'grid',
    gridTemplateColumns: '300px auto',
});

const Property = styled.span({
    color: aampeTheme.colors.gray800,
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '20px',
});

const PropertyValues = styled.div({
    display: 'flex',
    color: aampeTheme.colors.gray800,
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '20px',
    alignItems: 'center',
    gap: aampeTheme.spacing(1),
});

const PropertyDiv = styled.div({
    display: 'flex',
    gap: aampeTheme.spacing(2),
    flexDirection: 'column',
});

const PreviewContainer = styled.div({
    width: '60%',
});

const LabelList = styled.div({
    display: 'flex',
    gap: aampeTheme.spacing(1),
    flexWrap: 'wrap',
});

const LabelName = styled.span(({ color }: StyledProps) => ({
    color: color,
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '20px',
    marginTop: aampeTheme.spacing(1),
    alignSelf: 'start',
    width: 'max-content',
}));

const CalenderContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(2),
});

const ValueLink = styled.span({
    color: aampeTheme.colors.solidA01,
    fontSize: aampeTheme.size.font.default,
    fontWeight: 500,
    cursor: 'pointer',
    textDecoration: 'underline',
});
